// The lead-source export is one row per contact (~170K rows, 20+ MB), but the
// import only reads rows for patients who appear in the payments export. The
// browser drops the rest before upload, keeping each kept record's raw text
// byte-for-byte, so the Worker parses ~5% of the file within its memory limit.

const headerKey = (value: string): string =>
  value.replace(/^﻿/, "").trim().toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Splits CSV text into raw records, honouring quoted fields that contain
// commas or line breaks. Line endings are stripped from each record.
export const splitCsvRecords = (text: string): string[] => {
  const records: string[] = [];
  let start = 0;
  let inQuotes = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (char === '"') inQuotes = !inQuotes;
    else if (!inQuotes && (char === "\n" || char === "\r")) {
      records.push(text.slice(start, index));
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      start = index + 1;
    }
  }
  if (start < text.length) records.push(text.slice(start));
  return records.filter((record) => record.trim() !== "");
};

export const parseCsvRecord = (record: string): string[] => {
  const fields: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let index = 0; index < record.length; index += 1) {
    const char = record[index];
    if (inQuotes) {
      if (char === '"' && record[index + 1] === '"') { field += '"'; index += 1; }
      else if (char === '"') inQuotes = false;
      else field += char;
    } else if (char === '"') inQuotes = true;
    else if (char === ",") { fields.push(field); field = ""; }
    else field += char;
  }
  fields.push(field);
  return fields;
};

const patientColumn = (header: string): number =>
  parseCsvRecord(header).findIndex((cell) => headerKey(cell) === "patient id");

// Returns the lead-source CSV reduced to its header plus rows for patients in
// the payments CSV. If either file lacks a Patient ID column the lead file is
// returned unchanged, so the server's column validation reports the problem.
export const trimLeadSourcesCsv = (paymentsCsv: string, leadSourcesCsv: string): { csv: string; keptRows: number; totalRows: number } => {
  const payments = splitCsvRecords(paymentsCsv);
  const leads = splitCsvRecords(leadSourcesCsv);
  const unchanged = { csv: leadSourcesCsv, keptRows: Math.max(0, leads.length - 1), totalRows: Math.max(0, leads.length - 1) };
  if (payments.length < 2 || leads.length < 2) return unchanged;
  const paymentCol = patientColumn(payments[0]);
  const leadCol = patientColumn(leads[0]);
  if (paymentCol < 0 || leadCol < 0) return unchanged;
  const patients = new Set(payments.slice(1).map((record) => (parseCsvRecord(record)[paymentCol] ?? "").trim()).filter(Boolean));
  const kept = leads.slice(1).filter((record) => patients.has((parseCsvRecord(record)[leadCol] ?? "").trim()));
  return { csv: [leads[0], ...kept].join("\n") + "\n", keptRows: kept.length, totalRows: leads.length - 1 };
};

export type LeadCentre = "Istanbul" | "Cairo" | "Unallocated";
export type LeadCountRow = { month: string; leadSource: string; adAccount: string; centre: LeadCentre; count: number };

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

// "Nov 21, 2025 08:55 AM", "21 Sep 2026" or ISO → "2025-11".
export const leadMonth = (raw: string): string | null => {
  const value = raw.trim();
  let match = /^(\d{4})-(\d{2})-\d{2}/.exec(value);
  if (match) return Number(match[2]) >= 1 && Number(match[2]) <= 12 ? `${match[1]}-${match[2]}` : null;
  match = /^\d{1,2}\s+([A-Za-z]{3,})\.?\s+(\d{4})/.exec(value) ?? /^([A-Za-z]{3,})\.?\s+\d{1,2},\s*(\d{4})/.exec(value);
  if (!match) return null;
  const month = MONTHS.indexOf(match[1].slice(0, 3).toLowerCase());
  return month < 0 ? null : `${match[2]}-${String(month + 1).padStart(2, "0")}`;
};

// Leads are worked by the call centre that owns the contact: Cairo's agents
// and pool carry "Flypie" in the owner name, everyone else is Istanbul. On
// paying patients this matches the CRM branch ~97% of the time.
export const leadCentre = (owner: string): LeadCentre =>
  !owner.trim() ? "Unallocated" : /flypie/i.test(owner) ? "Cairo" : "Istanbul";

// Counts every contact in the full lead-source export (one lead per Patient
// ID, by the month the contact was created) before the file is trimmed for
// upload. Only these aggregates leave the browser.
export const countLeads = (leadSourcesCsv: string): { rows: LeadCountRow[]; contacts: number; undated: number } => {
  const records = splitCsvRecords(leadSourcesCsv);
  if (records.length < 2) return { rows: [], contacts: 0, undated: 0 };
  const header = parseCsvRecord(records[0]).map(headerKey);
  const col = (name: string) => header.indexOf(name);
  const [patient, source, contactAccount, account, created, owner] =
    ["patient id", "contact lead source", "contact ad account", "ad account", "contact created time", "contact owner"].map(col);
  if (patient < 0 || source < 0 || created < 0) return { rows: [], contacts: 0, undated: 0 };
  const seen = new Set<string>();
  const groups = new Map<string, LeadCountRow>();
  let undated = 0;
  for (const record of records.slice(1)) {
    const cells = parseCsvRecord(record);
    const id = (cells[patient] ?? "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const month = leadMonth(cells[created] ?? "");
    if (!month) { undated += 1; continue; }
    const row: LeadCountRow = {
      month,
      leadSource: (cells[source] ?? "").trim(),
      adAccount: ((contactAccount >= 0 ? cells[contactAccount] : "") ?? "").trim() || ((account >= 0 ? cells[account] : "") ?? "").trim(),
      centre: leadCentre(owner >= 0 ? cells[owner] ?? "" : ""),
      count: 0,
    };
    const key = JSON.stringify([row.month, row.leadSource, row.adAccount, row.centre]);
    const group = groups.get(key) ?? row;
    group.count += 1;
    groups.set(key, group);
  }
  return { rows: [...groups.values()], contacts: seen.size, undated };
};
